import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { detectarFormato, lerMes, linhasDaLista, linhasDoDreMensal, sugerirDestino, type Destino } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { Stepper } from "@/components/settings/import/Stepper";
import { FileDropzone } from "@/components/settings/import/FileDropzone";
import { lerPlanilha, type Planilha } from "./ler-planilha";
import { PassoColunas, colunasProntas, type ConfigDasColunas } from "./PassoColunas";
import { PassoCategorias } from "./PassoCategorias";
import { PassoPrevia } from "./PassoPrevia";
import { ListaDeImportacoes } from "./ListaDeImportacoes";
import { candidatosDa, gruposDosRotulos, montarLinhas } from "./montar";
import { useImportar } from "./use-importar";

const PASSOS = ["Arquivo", "Colunas", "Categorias", "Prévia"];
const botao = "h-9 rounded-md px-4 text-sm font-semibold disabled:opacity-50";

/** Importar lançamentos de uma planilha (lista ou DRE por mês), com mapeamento de colunas e de categorias. */
export function ImportarPlanilha({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const dadosDasCategorias = trpc.financeiro.categorias.listar.useQuery(undefined, { enabled: aberto }).data;
  const categorias = useMemo(() => dadosDasCategorias ?? [], [dadosDasCategorias]);
  const [passo, setPasso] = useState(0);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [planilha, setPlanilha] = useState<Planilha | null>(null);
  const [config, setConfig] = useState<ConfigDasColunas | null>(null);
  const [destinos, setDestinos] = useState<Record<string, Destino>>({});
  const [previa, setPrevia] = useState<{ fechados: string[]; faturamento: Record<string, number>; repetidos: number[] } | null>(null);
  const [incluirProducao, setIncluirProducao] = useState(false);
  const pedirPrevia = trpc.financeiro.importacao.previa.useMutation();
  const { importar, progresso, pendente } = useImportar();

  const lidas = useMemo(() => {
    if (!planilha || !config) return [];
    return config.formato === "LISTA"
      ? linhasDaLista(planilha.linhas, config.cabecalho, config.colunas, config.jaPagos)
      : linhasDoDreMensal(planilha.linhas, config.cabecalho, config.colunaDoRotulo, config.colunasDeMes, config.jaPagos);
  }, [planilha, config]);
  const grupos = useMemo(() => gruposDosRotulos(lidas), [lidas]);
  const montadas = useMemo(() => montarLinhas(lidas, destinos, categorias, previa), [lidas, destinos, categorias, previa]);

  const fechar = () => {
    setPasso(0);
    setArquivo(null);
    setPlanilha(null);
    setConfig(null);
    setPrevia(null);
    onFechar();
  };

  async function escolherArquivo(f: File | null) {
    setArquivo(f);
    if (!f) return;
    try {
      const p = await lerPlanilha(f);
      const { formato, cabecalho } = detectarFormato(p.linhas);
      const meses = (p.linhas[cabecalho] ?? []).flatMap((c, i) => (i > 0 && lerMes(c) ? [i] : []));
      setPlanilha(p);
      setConfig({ formato, cabecalho, colunas: {}, colunaDoRotulo: 0, colunasDeMes: meses, jaPagos: true });
      setPasso(1);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Não foi possível ler a planilha.");
    }
  }

  function irParaCategorias() {
    setDestinos(Object.fromEntries(grupos.map((g) => [g.rotulo, destinos[g.rotulo] ?? sugerirDestino(g.rotulo, categorias)])));
    setPasso(2);
  }

  async function irParaPrevia() {
    const candidatos = candidatosDa(lidas, destinos);
    const meses = [...new Set(candidatos.map((c) => c.competencia))];
    setPrevia(await pedirPrevia.mutateAsync({ meses, candidatos }));
    setPasso(3);
  }

  async function confirmar() {
    const entram = montadas.filter((l) => l.situacao === "ok" || (incluirProducao && l.situacao === "producao"));
    try {
      const r = await importar(planilha!.nome, config!.formato, entram);
      toast.success(`${r.criados} lançamentos importados. Dá para desfazer em Importações.`);
      fechar();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "A importação parou no meio. Veja em Importações o que entrou.");
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && !pendente && fechar()}>
      <DialogContent className="flex max-h-[90dvh] max-w-4xl flex-col gap-4 overflow-y-auto border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle>Importar planilha</DialogTitle>
          <DialogDescription>Lista de lançamentos (data, descrição, valor) ou DRE por mês (contas nas linhas, meses nas colunas).</DialogDescription>
        </DialogHeader>
        <Stepper steps={PASSOS} current={passo} />
        {passo === 0 && (
          <div className="space-y-4">
            <FileDropzone file={arquivo} onFile={escolherArquivo} />
            <ListaDeImportacoes />
          </div>
        )}
        {passo === 1 && planilha && config && <PassoColunas planilha={planilha} config={config} onChange={setConfig} />}
        {passo === 2 && <PassoCategorias grupos={grupos} destinos={destinos} categorias={categorias} onChange={(r, d) => setDestinos({ ...destinos, [r]: d })} />}
        {passo === 3 && <PassoPrevia linhas={montadas} incluirProducao={incluirProducao} onIncluirProducao={setIncluirProducao} />}
        {passo > 0 && (
          <div className="flex items-center justify-between gap-2 border-t border-zinc-100 pt-3 dark:border-border">
            <button type="button" disabled={pendente} onClick={() => setPasso(passo - 1)} className={`${botao} text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted`}>
              Voltar
            </button>
            {progresso && <span className="text-xs text-zinc-500">Importando {progresso.feitos} de {progresso.total}…</span>}
            {passo === 1 && (
              <button type="button" disabled={!config || !colunasProntas(config) || !lidas.length} onClick={irParaCategorias} className={`${botao} bg-brand-600 text-white hover:bg-brand-700`}>
                Continuar ({lidas.length} linhas)
              </button>
            )}
            {passo === 2 && (
              <button type="button" disabled={pedirPrevia.isPending} onClick={irParaPrevia} className={`${botao} bg-brand-600 text-white hover:bg-brand-700`}>
                Ver a prévia
              </button>
            )}
            {passo === 3 && (
              <button type="button" disabled={pendente || !montadas.some((l) => l.situacao === "ok" || (incluirProducao && l.situacao === "producao"))} onClick={confirmar} className={`${botao} bg-brand-600 text-white hover:bg-brand-700`}>
                Importar
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
